import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDeleteTeam, useTeam } from "@/hooks/queries/use-teams";
import { ApiError } from "@/lib/api/client";
import type { Team } from "@/lib/api/types";
import { AlertTriangle } from "lucide-react";

interface DeleteDisciplineDialogProps {
  teamId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  team?: Team;
  onDeleted?: () => void;
}

interface BlockerCounts {
  members: number;
  epicRequests: number;
  epics: number;
}

function hasPreflightCounts(team: Team | undefined): team is Team & {
  _count: {
    memberships: number;
    epicRequests: number;
    epics: number;
  };
} {
  return Boolean(
    team &&
    typeof team._count?.memberships === "number" &&
    typeof team._count?.epicRequests === "number" &&
    typeof team._count?.epics === "number"
  );
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const data = error.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? error.message ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function extractServerBlockers(error: unknown): BlockerCounts | null {
  if (!(error instanceof ApiError)) return null;

  const data = error.data as {
    error?: {
      details?: {
        blockers?: Partial<BlockerCounts>;
      };
    };
  } | undefined;

  const blockers = data?.error?.details?.blockers;
  if (!blockers) return null;

  return {
    members: blockers.members ?? 0,
    epicRequests: blockers.epicRequests ?? 0,
    epics: blockers.epics ?? 0,
  };
}

function formatBlockedExplanation(blockers: BlockerCounts): string {
  const parts = [
    blockers.members > 0 ? `${blockers.members} member${blockers.members === 1 ? "" : "s"}` : null,
    blockers.epicRequests > 0
      ? `${blockers.epicRequests} epic request${blockers.epicRequests === 1 ? "" : "s"}`
      : null,
    blockers.epics > 0 ? `${blockers.epics} active epic${blockers.epics === 1 ? "" : "s"}` : null,
  ].filter((part): part is string => part !== null);

  if (parts.length === 0) {
    return "This discipline can be deleted immediately.";
  }

  if (parts.length === 1) {
    return `Remove ${parts[0]} before deleting this discipline.`;
  }

  if (parts.length === 2) {
    return `Remove ${parts[0]} and ${parts[1]} before deleting this discipline.`;
  }

  return `Remove ${parts.slice(0, -1).join(", ")}, and ${parts.at(-1)} before deleting this discipline.`;
}

export function DeleteDisciplineDialog({ teamId, open, onOpenChange, team, onDeleted }: DeleteDisciplineDialogProps) {
  const [error, setError] = useState("");
  const [serverBlockers, setServerBlockers] = useState<BlockerCounts | null>(null);
  const deleteTeam = useDeleteTeam();
  const shouldLoadTeam = open && !hasPreflightCounts(team);
  const { data: loadedTeam, isLoading, isError } = useTeam(teamId, { enabled: shouldLoadTeam });

  const resolvedTeam = hasPreflightCounts(team) ? team : loadedTeam;
  const blockers = useMemo<BlockerCounts | null>(() => {
    if (serverBlockers) {
      return serverBlockers;
    }

    if (!hasPreflightCounts(resolvedTeam)) {
      return null;
    }

    return {
      members: resolvedTeam._count.memberships,
      epicRequests: resolvedTeam._count.epicRequests,
      epics: resolvedTeam._count.epics,
    };
  }, [resolvedTeam, serverBlockers]);

  const hasBlockers = Boolean(blockers && (blockers.members > 0 || blockers.epicRequests > 0 || blockers.epics > 0));
  const canDelete = Boolean(blockers) && !hasBlockers && !isLoading;
  const teamName = resolvedTeam?.name ?? team?.name ?? "this discipline";

  const handleDelete = async () => {
    setError("");

    try {
      await deleteTeam.mutateAsync(teamId);
      onOpenChange(false);
      onDeleted?.();
    } catch (deleteError) {
      setServerBlockers(extractServerBlockers(deleteError));
      setError(getErrorMessage(deleteError, "Failed to delete discipline"));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setError("");
          setServerBlockers(null);
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Delete Discipline</DialogTitle>
          <DialogDescription>
            Review the pre-flight checks before deleting "{teamName}". Disciplines with members, epic requests, or active epics must be emptied first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Members</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{blockers?.members ?? "—"}</p>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Epic Requests</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{blockers?.epicRequests ?? "—"}</p>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Active Epics</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{blockers?.epics ?? "—"}</p>
            </div>
          </div>

          {isError ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              Failed to load discipline data. Please close and try again.
            </div>
          ) : isLoading || !blockers ? (
            <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-muted-foreground">
              Checking discipline blockers...
            </div>
          ) : hasBlockers ? (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">Deletion blocked</p>
                  <p className="mt-1 text-amber-100/80">{formatBlockedExplanation(blockers)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              This discipline has no members, epic requests, or active epics. Deletion is safe to continue.
            </div>
          )}

          {error ? <div className="text-sm text-destructive">{error}</div> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={!canDelete || deleteTeam.isPending}
          >
            {deleteTeam.isPending ? "Deleting..." : hasBlockers ? "Cannot Delete Yet" : "Delete Discipline"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
